import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';

/**
 * Styled dropdown — the replacement for a native <select> anywhere in Depart.
 *
 * A native select renders with the operating system's own widget, which ignores
 * the app's palette entirely: on macOS it is a light popup in the middle of a
 * dark dashboard, and on Windows it is a different shape again. This looks the
 * same everywhere and matches the surrounding UI.
 *
 * The list is portalled for the same reason the airport picker's is — an
 * absolutely positioned menu gets clipped by any scrolling ancestor, and these
 * are used inside dialogs and cards that scroll.
 *
 * Keyboard behaviour matches a real select: arrows move, Enter and Space pick,
 * Escape closes, Home and End jump. Accessibility is not the reason to avoid a
 * native control, so a replacement has to earn it back rather than drop it.
 */
export default function RichSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  ariaLabel,
  minWidth = 0,
  align = 'left',
}) {
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const [rect, setRect] = useState(null);
  const btnRef = useRef(null);
  const listRef = useRef(null);
  const listId = useId();

  const selectedIndex = options.findIndex((o) => String(o.value) === String(value));
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;

  const measure = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = Math.max(r.width, minWidth);
    const below = window.innerHeight - r.bottom;
    const flip = below < 220 && r.top > below;
    const left = align === 'right' ? r.right - width : r.left;
    setRect({
      left: Math.max(8, Math.min(left, window.innerWidth - width - 8)),
      top: r.bottom + 5,
      bottom: window.innerHeight - r.top + 5,
      width, flip,
    });
  }, [minWidth, align]);

  useLayoutEffect(() => { if (open) measure(); }, [open, measure]);

  useEffect(() => {
    if (!open) return;
    const on = () => measure();
    window.addEventListener('scroll', on, true);
    window.addEventListener('resize', on);
    return () => { window.removeEventListener('scroll', on, true); window.removeEventListener('resize', on); };
  }, [open, measure]);

  useEffect(() => {
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target) || listRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // Open on the current value, not at the top — a native select does the same,
  // and a 90-item list that always opens at row 0 is tedious.
  useEffect(() => { if (open) setCursor(Math.max(0, selectedIndex)); }, [open, selectedIndex]);

  useEffect(() => {
    const el = listRef.current?.children?.[cursor];
    if (el?.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [cursor, open]);

  const choose = (o) => { onChange(o.value); setOpen(false); btnRef.current?.focus(); };

  const onKeyDown = (e) => {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) { e.preventDefault(); setOpen(true); }
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, options.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === 'Home') { e.preventDefault(); setCursor(0); }
    else if (e.key === 'End') { e.preventDefault(); setCursor(options.length - 1); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (options[cursor]) choose(options[cursor]); }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); btnRef.current?.focus(); }
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8, width: '100%',
          background: '#0f0f23', border: `1px solid ${open ? '#f9731660' : '#1e1e3a'}`,
          borderRadius: 9, color: selected ? '#ccc' : '#666', padding: '9px 11px',
          fontSize: 13, fontFamily: 'inherit', cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown size={13} style={{ color: '#555', flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
      </button>

      {open && rect && createPortal(
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          style={{
            position: 'fixed', left: rect.left, width: rect.width,
            ...(rect.flip ? { bottom: rect.bottom } : { top: rect.top }),
            zIndex: 10060, maxHeight: 260, overflowY: 'auto',
            background: '#0f0f23', border: '1px solid #2a2a4a', borderRadius: 10,
            boxShadow: '0 14px 34px #00000080', padding: 4,
          }}
        >
          {options.map((o, i) => {
            const active = String(o.value) === String(value);
            return (
              <button
                key={String(o.value)}
                type="button"
                role="option"
                aria-selected={active}
                onMouseEnter={() => setCursor(i)}
                onClick={() => choose(o)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  background: i === cursor ? '#f9731615' : 'transparent',
                  border: 'none', borderRadius: 7, padding: '8px 10px', cursor: 'pointer',
                  color: active ? '#f97316' : '#ccc', fontSize: 12.5, fontWeight: active ? 700 : 500,
                  textAlign: 'left', fontFamily: 'inherit',
                }}
              >
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {o.label}
                </span>
                {o.sub && <span style={{ color: '#555', fontSize: 10, flexShrink: 0 }}>{o.sub}</span>}
                {active && <Check size={13} style={{ flexShrink: 0 }} />}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
}
